import { Component, Vue, Watch } from "vue-property-decorator";
import { Select, Switch, Input, Button, Spin, Alert, Form } from "ant-design-vue";
import { getModule } from "vuex-module-decorators";
import TitleListState from "@/modules/title-list";
import { deepClone, isAnyVersionSelected } from "@/util/func";
import TextAnalysisGrid from "../components/text-grid/text-analysis-grid.vue";
import { GridCols } from "../components/text-grid/text-analysis-grid";
import {
	RWTextAnalaysisChapterInfoDto,
	RWTextAnalysisResponseModel,
	RwTextAnalysisSeriesDto,
	TextAnalysisService
} from "@/services/vcs-report";
import { VersionGroupModule } from "@/modules/versiongroup";
import { fmtMsg, isGuid } from "commonui";
import { VCSLevelModel } from "@/models/title-list-model";
import { RWTextAnalysisSeriesChapterModel } from "@/services/vcs-report";
import { max, min, range } from "lodash";
import { ReadWriteTextAnalysisLocale } from "@/locales/localeid";
import { WordManagerService } from "@/services/word-manager-service";
import { RWUnitGroupsReponseModel } from "@/models/word-manager-model";
import PageTitle from "@/components/page-title/page-title.vue";
import { CurriculumType } from "@/util/enum";

@Component({
	components: {
		Select,
		SelectOption: Select.Option,
		SwitchEl: Switch,
		Textarea: Input.TextArea,
		TextAnalysisGrid,
		Button,
		Spin,
		Alert,
		Form,
		PageTitle
	}
})
export default class ReaderWriterTextAnalysis extends Vue {
	titleListModule = getModule(TitleListState);
	versionGroupModule = VersionGroupModule;
	public readonly lableSpan = 6;
	public readonly itemSpan = 18; // for multiple cols
	public readonly lableSpanTwo = 3; // for single cols
	public readonly itemSpanTwo = 21;

	public allowedLevels: VCSLevelModel[] = [];
	public allSeriesChapters: RWTextAnalysisSeriesChapterModel[] = [];
	public seriesList: string[] = [];
	public seriesSelected: string = "";
	public chapterSelected: string = "";
	public seriesChapter: RWTextAnalaysisChapterInfoDto[] = [];
	public lemmaOn: boolean = true;
	public loading = false;
	public unit = 0; /// levelId currently selected
	public wordList = "";
	public defaultSource: RWTextAnalysisResponseModel = {
		assignedRWWords: [],
		previousRWWords: [],
		familiarWordsRW: [],
		familiarWordsVS: [],
		futureWordsRW: [],
		futureWordsVS: [],
		nonCurriculumWords: []
	};
	public noVersion: boolean = false;
	public locale = ReadWriteTextAnalysisLocale;
	public format = fmtMsg;
	public form;

	public source = deepClone(this.defaultSource);

	public baseCols: GridCols[] = [
		{
			bindTo: "word",
			heading: "Word",
			render: (word: string, { isLemma }: { isLemma: boolean }): string => {
				const words = this.processWordList(this.wordList).split(",");
				if (isLemma) {
					return word + "*";
				}
				return word;
			}
		},
		{
			bindTo: "occurrences",
			heading: "Occurrence(s)"
		},
		{
			bindTo: "lsExposureUnit",
			heading: "LS First Unit"
		},
		{
			bindTo: "gsExposureUnit",
			heading: "GS First Unit"
		},
		{
			bindTo: "seriesCount",
			heading: "Count in Series"
		},
		{
			bindTo: "totalOccurrences",
			heading: "Total occurrences in entire Curriculum"
		}
	];

	public nonCurriculumCols: GridCols[] = [deepClone(this.baseCols[0]), deepClone(this.baseCols[1])];
	public rwUnitSelectedGroups: RWUnitGroupsReponseModel[] = [];
	public rwUnitGroupSequencesSelected: number[] | null = [];

	@Watch("versionGroupModule.selectedGsVersion")
	refetch() {
		this.fetchChapters();
		this.source = [];

		if (!isGuid(this.versionGroupModule.selectedGsVersion)) {
			this.noVersion = true;
		} else {
			this.noVersion = false;
		}
	}

	@Watch("versionGroupModule.disabled", { immediate: true })
	enable() {
		VersionGroupModule.disabled && VersionGroupModule.setDisabled(false);
	}

	@Watch("unit")
	unitChanged() {
		this.unitUpdated();
	}

	created() {
		if (!isGuid(this.versionGroupModule.selectedGsVersion)) {
			this.noVersion = true;
		}
		this.form = this.$form.createForm(this, { name: "form" });
		this.fetchChapters();
	}

	beforeDestroy() {
		VersionGroupModule.setDisabled(true);
	}

	fetchChapters() {
		this.loading = true;
		TextAnalysisService.getRWTextAnalysisSeriesChapter(VersionGroupModule.selectedGsVersion)
			.then((data: RWTextAnalysisSeriesChapterModel[]) => {
				this.allSeriesChapters = data;
				const dataMaxUnit = max(data.map((m) => m.unit));
				const dataMinUnit = min(data.map((m) => m.unit));
				if (this.titleListModule.levels.length) {
					this.allowedLevels = this.titleListModule.levels.filter(
						(l) =>
							l.unit <= dataMaxUnit! &&
							l.unit >= dataMinUnit! &&
							l.curriculumType === CurriculumType.GrapeSEED
					);
					this.unit = this.allowedLevels.length ? this.allowedLevels[0].id : -1;
				}

				const unit = this.titleListModule.levels.find((l) => l.id === this.unit)?.unit as number;
				const currentSeries = data.find((d) => d.unit === unit)?.series[0] as RwTextAnalysisSeriesDto;
				this.seriesSelected = currentSeries.name as string;
				this.seriesList = data.find((d) => d.unit == unit)?.series.map((m) => m.name) as string[];
				this.seriesChapter = currentSeries.chapters;
				this.chapterSelected = this.seriesChapter.length ? this.seriesChapter[0].id : "";
				this.unitUpdated();
				this.loading = false;
			})
			.catch(() => {
				this.source = deepClone(this.defaultSource);
				this.loading = false;
			});
	}

	unitUpdated() {
		if (this.unit !== 0) {
			// Find Unit Id
			const unitId = this.allowedLevels.find((level) => level.unit === this.unit)?.id;
			this.loading = true;
			WordManagerService.getRWUnitGroups(unitId)
				.then((data) => {
					if (data.length) {
						this.rwUnitSelectedGroups = data;
					} else {
						this.rwUnitSelectedGroups = data;
					}
					this.loading = false;
					this.rwUnitGroupSequencesSelected = [];
				})
				.catch(() => (this.loading = false));
		}
	}

	fetchReport(e?) {
		if (e) {
			e.preventDefault();
		}

		this.loading = true;
		TextAnalysisService.getRWTextAnalysisReport({
			grapeseedVersion: isGuid(VersionGroupModule.selectedGsVersion)
				? VersionGroupModule.selectedGsVersion
				: undefined,
			isLemmaOn: this.lemmaOn,
			unit: this.titleListModule.levels.find((l) => l.id === this.unit)?.unit as number,
			userWords: this.processWordList(this.wordList),
			chapterId: this.chapterSelected,
			rwGroupSequence: this.rwUnitGroupSequencesSelected
		})
			.then((data) => {
				this.source = data;
				this.loading = false;
			})
			.catch(() => {
				this.source = deepClone(this.defaultSource);
				this.loading = false;
			});
	}

	processWordList(words: string) {
		const special = ":;\"<>?/!`~@#$%^&*()+=-_.{}[]|\\' ".split("");
		const trim = (input: string) => {
			const inTrim = (str: string) => {
				const spStr = str.split("");
				let deleteTill = 0;
				let startChar = spStr[deleteTill];
				while (special.some((s) => s === startChar)) {
					deleteTill++;
					if (deleteTill <= spStr.length) {
						startChar = spStr[deleteTill];
					} else {
						deleteTill--;
						break;
					}
				}
				spStr.splice(0, deleteTill);
				return spStr.join("");
			};
			input = inTrim(input);
			input = inTrim(input.split("").reverse().join("")).split("").reverse().join("");
			return input;
		};
		const list = words
			.replace(/,/gm, " ")
			.replace(/[\r\n]+|[\r]+|[\n]+|[—]+/g, " ")
			.split(" ")
			.filter((w) => w.trim().length)
			.map((w) => trim(w.trim().toLowerCase()))
			.filter((w) => w.trim().length)
			.join(",");
		return list ? list : "";
	}

	selectControlContainer(element: HTMLDivElement) {
		return element.parentNode;
	}

	onChapterChange(value: string) {
		this.chapterSelected = value;
	}

	onUnitChange(unitId: number) {
		this.unit = unitId;
		const unit = this.titleListModule.levels.find((l) => l.id === unitId)?.unit as number;
		const currentSeries = this.allSeriesChapters.find((d) => d.unit === unit)?.series[0] as RwTextAnalysisSeriesDto;
		this.seriesSelected = currentSeries.name as string;
		this.seriesList = this.allSeriesChapters.find((d) => d.unit == unit)?.series.map((m) => m.name) as string[];
		this.seriesChapter = currentSeries.chapters;
		this.chapterSelected = this.seriesChapter.length ? this.seriesChapter[0].id : "";
	}

	onSeriesChange(seriesName: string) {
		this.seriesSelected = seriesName;
		const unit = this.titleListModule.levels.find((l) => l.id === this.unit)?.unit as number;
		const currentSeries = this.allSeriesChapters
			.find((d) => d.unit === unit)
			?.series.find((s) => s.name === seriesName) as RwTextAnalysisSeriesDto;
		this.seriesChapter = currentSeries.chapters;
		this.chapterSelected = this.seriesChapter.length ? this.seriesChapter[0].id : "";
	}

	onRwGroupChange(value) {
		this.rwUnitGroupSequencesSelected = value;
	}
}
